import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import {
  prepareImage,
  prepareOriginalImage,
  attachmentUrl,
  photoHoldMessage,
} from '../../lib/image';
import { uploadQueue, useQueuedPhotos } from '../../lib/upload-queue';
import { useSession } from '../session';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { IconPlus, IconTrash, IconX } from '../icons';
import { isForbidden } from '../ui/QueryStates';
import { sameAccount } from '../../lib/account';
import type { MediaProcessingState, MediaHoldCode } from '@servwell/shared';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';

type Photo = {
  id: string;
  filename: string | null;
  width: number | null;
  height: number | null;
  takenAt: string | Date | null;
  createdAt: string | Date;
  byName: string | null;
  /**
   * Set on a photo still held on this phone. One `Photo` type and one render path on
   * purpose: a separate "queued photos" grid beside the real one is how the two drift, and
   * from a tech's side of the screen they are the same photograph either way.
   *
   * It is its own field rather than `localSrc !== undefined`, because it decides where the
   * remove button sends a tap. Branching a server call on whether a *string* happens to be
   * present is right only for as long as the two can never disagree.
   */
  queued?: boolean;
  /** Its own bytes, for a photo that has not reached the server yet. */
  localSrc?: string;
  /** On the wire right now. It is going to land, so it cannot be taken back. */
  sending?: boolean;
  /** The server's reason for refusing it, if it did. */
  failure?: string | null;
  /** Held for a form not sent yet: it goes when the form does. */
  parked?: boolean;
  managedOriginal?: boolean;
  originalAvailable?: boolean;
  processingState?: MediaProcessingState;
  holdCode?: MediaHoldCode;
  retryAt?: number;
};

/**
 * Photos (PRD v2 §5) — the same grid at the door and at the desk.
 *
 * This is the cheapest evidence a shop can collect and the one it regrets not having: the
 * heat exchanger it condemned, the crawlspace before anyone went in, the model plate nobody
 * could read from the office. The server policy selects original preservation or the
 * established legacy preparation. Either path keeps bytes durably before touching the network.
 *
 * Which is exactly what used to happen with no signal at all: the upload was a bare `fetch`,
 * so it failed, said `Failed to fetch`, and the photograph was gone. Every photo now goes
 * into `uploadQueue` before the network is touched, and stays on the phone until the server
 * has actually taken it.
 */
export function PhotoGrid({
  entityType,
  entityId,
  /** The office view of a job also shows what techs attached to its visits. */
  jobId,
  title = 'Photos',
  compact = false,
  parked = false,
}: {
  /** `ticket`: the Tickets panel's photos (ops insights 5a), added from the office. */
  entityType: 'job' | 'visit' | 'ticket' | 'quote';
  entityId: string;
  jobId?: string;
  title?: string;
  compact?: boolean;
  /**
   * Photos for a form not sent yet ("Flag a problem", ticket 03): kept on this phone and
   * shown here, but held back from sending until the form is (`uploadQueue.release`). The
   * record they name does not exist yet, so nothing is asked of the server either.
   */
  parked?: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const me = useSession();
  const fileInput = useRef<HTMLInputElement>(null);
  const account = useRef(me);
  account.current = me;
  const policy = useQuery(trpc.attachment.mediaPolicy.queryOptions());
  const [busy, setBusy] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  useEffect(() => {
    setViewing(null);
    setError(null);
    setBusy(0);
  }, [me.userId, me.orgId]);

  // Two queries rather than one branching call: the office asks a different question
  // (every photo across the job's visits) than the phone does (this stop's).
  const wholeJob = useQuery({
    ...trpc.attachment.forJob.queryOptions({ jobId: jobId ?? entityId }),
    enabled: Boolean(jobId),
    refetchInterval: (q) =>
      q.state.data?.some(
        (p) => p.processingState === 'pending' || p.processingState === 'processing',
      )
        ? 10_000
        : false,
  });
  const thisEntity = useQuery({
    ...trpc.attachment.forEntity.queryOptions({ entityType, entityId }),
    enabled: !jobId && !parked,
    refetchInterval: (q) =>
      q.state.data?.some(
        (p) => p.processingState === 'pending' || p.processingState === 'processing',
      )
        ? 10_000
        : false,
  });
  const q = jobId ? wholeJob : thisEntity;
  // A parked grid asks the server nothing (a disabled query stays `pending`, and cannot fail),
  // so it is never loading.
  const loading = !parked && q.isPending;

  const remove = useMutation(
    trpc.attachment.remove.mutationOptions({
      onSuccess: () => {
        setError(null);
        void qc.invalidateQueries();
      },
      onError: (e) => setError(e.message),
    }),
  );

  const onPick = async (files: FileList | null) => {
    if (!files?.length || !policy.data) return;
    setError(null);
    const chosen = [...files];
    setBusy(chosen.length);
    const failures: string[] = [];
    // One at a time, and `busy` now covers only the downscale: the send itself belongs to the
    // queue, which is serial for the same reason this loop always was — four photos in
    // parallel over LTE finish later than four in turn.
    for (const file of chosen) {
      try {
        const image = policy.data.enabled
          ? await prepareOriginalImage(file, policy.data.maxOriginalBytes)
          : await prepareImage(file);
        if (!sameAccount(account.current, me)) return;
        await uploadQueue.add({
          // Whose photo this is. A shop tablet two techs sign into shares one queue, and a
          // photo drained under the wrong cookie is filed under the wrong name.
          userId: me.userId,
          orgId: me.orgId,
          entityType,
          entityId,
          filename: image.filename,
          contentType: image.contentType,
          width: image.width,
          height: image.height,
          takenAt: image.takenAt?.toISOString() ?? null,
          blob: image.blob,
          ...('sha256' in image
            ? { protocol: 'managed-photo-v1' as const, sha256: image.sha256 as string }
            : {}),
          parked,
        });
      } catch (err) {
        // The only failures left here are the phone refusing to keep it — a full queue or
        // no room on the device. Both mean this photo was NOT kept, so say so while the tech
        // is still standing in front of the thing they photographed.
        failures.push((err as Error).message);
      } finally {
        if (sameAccount(account.current, me)) setBusy((n) => Math.max(0, n - 1));
      }
    }
    if (!sameAccount(account.current, me)) return;
    if (failures.length > 0) {
      setError(
        failures.length === 1
          ? failures[0]!
          : `${failures.length} photos weren't kept: ${failures[0]}`,
      );
    }
    if (fileInput.current) fileInput.current.value = ''; // so the same file can be re-picked
  };

  const allQueued = useQueuedPhotos(entityType, entityId);
  const queued = useMemo(
    () => allQueued.filter((p) => sameAccount(me, p)),
    [allQueued, me.userId, me.orgId],
  );

  /**
   * A queued photo is shown from its own bytes. Revoked when the set changes, because an
   * object URL that is never released holds the whole image in memory for the life of the
   * tab — on a phone, for a day of photographs.
   */
  const localSrcs = useMemo(
    () => new Map(queued.map((p) => [p.mutationId, URL.createObjectURL(p.blob)])),
    [queued],
  );
  useEffect(
    () => () => {
      for (const url of localSrcs.values()) URL.revokeObjectURL(url);
    },
    [localSrcs],
  );

  const stored = (parked ? [] : (q.data ?? [])) as Photo[];
  // Newest first, and nothing on this phone is older than anything the server already has.
  const photos: Photo[] = [
    ...queued.map((p) => ({
      id: p.mutationId,
      filename: p.filename,
      width: p.width,
      height: p.height,
      takenAt: p.takenAt,
      createdAt: new Date(p.queuedAt),
      byName: null,
      queued: true,
      sending: p.sending,
      localSrc: localSrcs.get(p.mutationId),
      failure: p.failure,
      parked: p.parked,
      holdCode: p.holdCode,
      retryAt: p.retryAt,
    })),
    ...stored,
  ];
  const cols = compact ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-5';

  return (
    <SectionCard
      title={photos.length === 0 ? title : `${title} (${photos.length})`}
      right={
        <Button
          variant="ghost"
          disabled={busy > 0 || !policy.data}
          onClick={() => fileInput.current?.click()}
        >
          <IconPlus size={14} />
          {/* Preparing, not sending: with no signal this photo is being KEPT, and a
              button that says "Sending" while nothing can send is the lie next door. */}
          {busy > 0 ? `Adding ${busy}…` : 'Add photos'}
        </Button>
      }
    >
      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
        multiple
        // `capture` is deliberately NOT set: a tech often wants the shot they already took
        // in the camera app, and forcing the camera hides the library.
        className="hidden"
        aria-label="Add photos"
        onChange={(e) => onPick(e.target.files)}
      />

      {policy.data ? (
        <p className="mb-3 text-xs text-muted-foreground">
          {policy.data.enabled
            ? `Original photos up to ${policy.data.maxOriginalBytes / 1_000_000} MB each. Photos waiting to send stay on this device.`
            : `Photos are prepared for upload, up to ${policy.data.maxOriginalBytes / 1_000_000} MB per upload.`}
        </p>
      ) : (
        <p className="mb-3 text-sm text-muted-foreground">
          {policy.isPaused
            ? 'Connect once to load this company’s photo settings.'
            : policy.isError
              ? 'Couldn’t load photo settings.'
              : 'Loading photo settings…'}{' '}
          {policy.isError ? (
            <button
              type="button"
              className="underline"
              onClick={() => void policy.refetch()}
            >
              Try again
            </button>
          ) : null}
        </p>
      )}
      {error ? <Notice tone="danger">{error}</Notice> : null}

      {/* Additive, not a branch: failing to load what the server has says nothing about the
          photos on this phone, and hiding those is how one looks lost. */}
      {q.isError ? (
        isForbidden(q.error) ? (
          // A 403 became possible on this query for the first time in ticket 031, when file
          // reads started riding the permission on the record they hang off. Retrying a
          // refusal is a button that can only fail, and it reads as "the app is broken"
          // rather than "this isn't your account" — the exact anti-pattern QueryStates names.
          <p className="text-sm text-muted-foreground">
            You don't have access to these photos.
          </p>
        ) : (
          <p className="text-sm text-[var(--sw-danger-text)]">
            Couldn't load the photos.{' '}
            <button type="button" className="underline" onClick={() => void q.refetch()}>
              Try again
            </button>
          </p>
        )
      ) : null}

      {loading && photos.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : // "No photos yet" is a claim about the server, and a failed load has not made it.
      q.isError && photos.length === 0 ? null : photos.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {busy > 0
            ? 'Getting them ready…'
            : 'No photos yet. What you photograph today is what you can prove in six months.'}
        </p>
      ) : (
        <div className={`grid gap-2 ${cols}`}>
          {photos.map((p) => (
            <figure key={p.id} className="group relative">
              <button
                type="button"
                className="block w-full overflow-hidden rounded-[var(--radius)] border border-border bg-surface-2"
                onClick={() => setViewing(p.id)}
                aria-label={
                  p.queued
                    ? `Open ${p.filename ?? 'photo'} — ${p.failure ? 'not accepted' : p.sending ? 'sending' : 'waiting to send'}`
                    : `Open ${p.filename ?? 'photo'}`
                }
              >
                <PhotoPreview photo={p} variant="thumbnail" />
              </button>
              <button
                type="button"
                aria-label={`Remove ${p.filename ?? 'photo'}`}
                title="Remove"
                /*
                 * 44px minimum, always visible. Two things this can't be: hover-revealed
                 * (there is no hover on the phone this is mostly used from) or the 25px
                 * dot that looks right on a desk — a gloved thumb in a driveway cannot hit
                 * a 25px target, and it costs a corner of a thumbnail to fix.
                 */
                className="absolute right-0.5 top-0.5 flex min-h-11 min-w-11 items-center justify-center rounded-full text-muted-foreground hover:text-text"
                // A photo still on the phone is dropped locally, so it is never waiting on a
                // server call and must not be disabled by one. This is the asymmetry the
                // ticket names, closed: taking one and taking it back now behave alike.
                // A photo on the wire is going to land whatever this button does, so
                // removing it here would make it vanish and then come back from the server
                // a second later. It becomes removable the normal way the moment it lands.
                disabled={p.sending || (!p.queued && remove.isPending)}
                onClick={() => {
                  if (!p.queued) {
                    remove.mutate({ attachmentId: p.id, ...idem() });
                    return;
                  }
                  // Removing a STORED photo soft-deletes the row and keeps the file, on
                  // purpose: an accidental tap must not destroy the only picture of
                  // something somebody will argue about later. A photo that has not sent
                  // yet exists in exactly one place, so the same tap is far worse here —
                  // which is why this one asks and that one does not.
                  if (
                    window.confirm(
                      'Delete this photo? It hasn’t sent yet, so this is the only copy Servwell has.',
                    )
                  ) {
                    void uploadQueue
                      .drop(p.id)
                      .catch((e) => setError((e as Error).message));
                  }
                }}
              >
                <span className="rounded-full bg-[var(--sw-surface)]/90 p-1.5 shadow-[var(--shadow-sm)]">
                  <IconTrash size={14} />
                </span>
              </button>
              <figcaption
                className={`mt-1 break-words text-xs ${p.failure ? 'text-[var(--sw-danger-text)]' : 'text-muted-foreground'}`}
                title={p.failure ?? undefined}
              >
                {p.failure ? (
                  <>
                    {p.failure}{' '}
                    {/* `retry()` existed with no caller until this line. A refusal can go
                        away — a stop gets reassigned, a deploy fixes a 415 — and without
                        this the only control offered on a refused photo destroys it. */}
                    <button
                      type="button"
                      className="inline-flex min-h-11 items-center underline"
                      onClick={() =>
                        void uploadQueue
                          .retry(p.id)
                          .catch((e) => setError((e as Error).message))
                      }
                    >
                      Try again
                    </button>
                  </>
                ) : p.sending ? (
                  'Sending…'
                ) : p.parked ? (
                  'Sends with the problem'
                ) : p.retryAt ? (
                  `${photoHoldMessage(p.holdCode)} Next try after ${new Date(p.retryAt).toLocaleTimeString()}.`
                ) : p.queued ? (
                  'On this phone — sends when you’re back'
                ) : (
                  <>
                    {new Date(p.takenAt ?? p.createdAt).toLocaleDateString()}
                    {/* On the phone the photographer is always the person holding it, and a
                        three-across caption has no room to say so. */}
                    {!compact && p.byName ? ` · ${p.byName.split(' ')[0]}` : ''}
                  </>
                )}
              </figcaption>
              {p.queued || p.originalAvailable ? (
                <a
                  className="inline-flex min-h-11 items-center text-xs underline"
                  href={p.localSrc ?? attachmentUrl(p.id, 'original')}
                  download={p.filename ?? 'photo'}
                >
                  {p.queued ? 'Download unsent photo' : 'Download original'}
                </a>
              ) : null}
            </figure>
          ))}
          {/* A placeholder while a picked file is being downscaled, so the grid doesn't sit
              still and leave a tech wondering whether the tap registered. */}
          {Array.from({ length: busy }).map((_, i) => (
            <div
              key={`pending-${i}`}
              className="aspect-square animate-pulse rounded-[var(--radius)] border border-border bg-surface-2"
            />
          ))}
        </div>
      )}

      {viewing ? (
        <Lightbox
          photos={photos}
          id={viewing}
          onClose={() => setViewing(null)}
          onMove={setViewing}
        />
      ) : null}
    </SectionCard>
  );
}

/** Decoding a preview is optional; preserving the original is not. */
function PhotoPreview({
  photo,
  variant,
}: {
  photo: Photo;
  variant: 'thumbnail' | 'viewer';
}) {
  const src =
    photo.localSrc ??
    attachmentUrl(photo.id, photo.managedOriginal ? variant : undefined);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const pending =
    !photo.queued &&
    photo.managedOriginal &&
    (photo.processingState === 'pending' || photo.processingState === 'processing');
  const unavailable = failed || (!photo.queued && photo.processingState === 'failed');
  if (pending || unavailable)
    return (
      <span className="flex min-h-40 items-center justify-center p-4 text-center text-sm text-muted-foreground">
        {pending
          ? 'Preparing preview · original saved'
          : photo.queued
            ? 'Preview unavailable on this device · original kept'
            : 'Preview unavailable · original saved'}
      </span>
    );
  return (
    <img
      src={src}
      alt={photo.filename ?? 'Job photo'}
      loading="lazy"
      onError={() => setFailed(true)}
      className={
        variant === 'thumbnail'
          ? `aspect-square w-full object-cover ${photo.queued ? 'opacity-70' : ''}`
          : 'max-h-full max-w-full object-contain'
      }
    />
  );
}

/** Full-size, with arrows — because the reason to open a photo is to look at the detail. */
function Lightbox({
  photos,
  id,
  onClose,
  onMove,
}: {
  photos: Photo[];
  id: string;
  onClose: () => void;
  onMove: (id: string) => void;
}) {
  const index = photos.findIndex((p) => p.id === id);
  const photo = photos[index];
  const opener = useRef(document.activeElement as HTMLElement | null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && photos[index + 1]) onMove(photos[index + 1]!.id);
      if (e.key === 'ArrowLeft' && photos[index - 1]) onMove(photos[index - 1]!.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, photos, onClose, onMove]);

  if (!photo) return null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="inset-0 flex h-dvh w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 bg-black/95 p-0 sm:max-w-none"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          if (opener.current?.isConnected) opener.current.focus();
        }}
      >
        <DialogTitle className="sr-only">{photo.filename ?? 'Photo'}</DialogTitle>
        {/* Its own solid bar: at anything less than opaque the shell header underneath shows
          through and the two lines of text tangle. Padded past the notch on a phone. */}
        <div
          className="flex items-center justify-between gap-2 bg-black px-4 py-3 text-sm text-white"
          style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
        >
          <span className="min-w-0 truncate">
            {index + 1} of {photos.length}
            {photo.byName ? ` · ${photo.byName}` : ''} ·{' '}
            {new Date(photo.takenAt ?? photo.createdAt).toLocaleString()}
            {photo.queued ? ' · On this phone, not sent yet' : ''}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full hover:bg-white/15"
          >
            <IconX size={18} />
          </button>
        </div>
        <div
          className="flex min-h-0 flex-1 items-center justify-center gap-2 p-2"
          onClick={(e) => e.stopPropagation()}
        >
          <NavButton
            dir="prev"
            disabled={index === 0}
            onClick={() => photos[index - 1] && onMove(photos[index - 1]!.id)}
          />
          <PhotoPreview photo={photo} variant="viewer" />
          <NavButton
            dir="next"
            disabled={index === photos.length - 1}
            onClick={() => photos[index + 1] && onMove(photos[index + 1]!.id)}
          />
        </div>
        {photo.queued || photo.originalAvailable ? (
          <a
            className="mb-3 flex min-h-11 items-center justify-center text-sm text-white underline"
            href={photo.localSrc ?? attachmentUrl(photo.id, 'original')}
            download={photo.filename ?? 'photo'}
          >
            {photo.queued ? 'Download unsent photo' : 'Download original'}
          </a>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function NavButton({
  dir,
  disabled,
  onClick,
}: {
  dir: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === 'prev' ? 'Previous photo' : 'Next photo'}
      className="shrink-0 rounded-full p-3 text-2xl text-white disabled:opacity-25"
    >
      {dir === 'prev' ? '‹' : '›'}
    </button>
  );
}
